import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, Building2, ArrowRight, Sparkles } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import Navbar from "@/components/tchatix/Navbar";
import Footer from "@/components/tchatix/Footer";
import { useTranslation } from "react-i18next";
import { JOBS } from "@/data/careers";

function JobListItem({ job, t }) {
  return (
    <Link
      to={`/recrutement/${job.slug}`}
      className="tx-glass rounded-2xl p-5 flex items-start gap-4 hover:border-[#0985D8]/40 transition-colors group">
      
      <div className="w-11 h-11 rounded-xl bg-[#0985D8]/15 text-[#0985D8] flex items-center justify-center shrink-0">
        <Briefcase className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg font-semibold text-[#001D31] [font-family:'Playfair_Display',_serif] mb-1.5">
          {job.title}
        </h3>
        <div className="flex flex-wrap items-center gap-3 text-xs text-[#8896B0]">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {job.location}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {job.contract} · {job.type}
          </span>
          <span className="inline-flex items-center gap-1">
            <Building2 className="w-3.5 h-3.5" /> {job.remote}
          </span>
        </div>
        <p className="text-sm text-[#8896B0] mt-2">{job.excerpt}</p>
      </div>
      <ArrowRight className="w-5 h-5 text-[#0985D8] shrink-0 mt-1 transition-transform group-hover:translate-x-1" />
    </Link>);

}

export default function Recrutement() {
  const { t } = useTranslation();

  return (
    <div className="relative min-h-screen text-[#001D31] overflow-x-hidden">
      <Seo
        title={t("careers.metaTitle")}
        description={t("careers.metaDesc")}
        path="/recrutement"
        jsonLd={breadcrumbLd([
        { name: t("careers.breadcrumbHome"), path: "/" },
        { name: t("careers.breadcrumbCareers"), path: "/recrutement" }]
        )} />
      
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-36 pb-16 px-6 overflow-hidden tx-grid-bg">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#0985D8] opacity-[0.06] blur-[130px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full tx-glass-strong text-sm text-[#0985D8] mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#FF5A3C]" /> {t("careers.badge")}
            </span>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight mb-6 [font-family:'Playfair_Display',_serif]">
              {t("careers.titleLine1")}
              <br />
              <span className="tx-gradient-text">{t("careers.titleLine2")}</span>
            </h1>
            <p className="text-[#8896B0] leading-relaxed text-lg">
              {t("careers.subtitle")}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-6 text-sm text-[#001D31]">
              <span className="px-3 py-1.5 rounded-full tx-glass">
                {JOBS.length} {JOBS.length > 1 ? t("careers.openPlural") : t("careers.openSingle")}
              </span>
              

              
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Postes ouverts ── */}
      <section id="postes" className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold mb-6 [font-family:'Playfair_Display',_serif]">
            {t("careers.openTitle")}
          </h2>
          <div className="space-y-4">
            {JOBS.map((job) =>
            <JobListItem key={job.slug} job={job} t={t} />
            )}
          </div>

          <p className="text-sm text-[#8896B0] mt-8 text-center">
            {t("careers.spontaneous")}{" "}
            <a href="mailto:contact@tchatix.com" className="text-[#0985D8] font-medium hover:underline">contact@tchatix.com

            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>);

}